/**
 * Scheduler Authentication Context
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { authAPI } from '../utils/scheduler/api'
import { setUser, clearAuth, getUser, isSessionExpired } from '../utils/scheduler/auth'
import { hasPermission, getAccessibleApps, HUB_APP_PERMISSIONS } from '../constants/hub-apps'
import { isSchedulerAdmin } from '../constants/hub-groups'

const SchedulerAuthContext = createContext(null)

export function SchedulerAuthProvider({ children }) {
  const [user, setUserState] = useState(null)
  const [loading, setLoading] = useState(true)
  const sessionCheckInterval = useRef(null)

  // Check session expiration periodically (every 5 minutes)
  useEffect(() => {
    const checkSession = () => {
      if (user && isSessionExpired()) {
        // Session expired, clear auth and redirect to login
        clearAuth()
        setUserState(null)
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/hub/login' && window.location.pathname !== '/scheduler/login') {
          window.location.href = '/hub/login'
        }
      }
    }

    // Check immediately
    checkSession()

    // Set up interval to check every 5 minutes
    sessionCheckInterval.current = setInterval(checkSession, 5 * 60 * 1000)

    // Listen for session expiration events from API
    const handleSessionExpired = () => {
      clearAuth()
      setUserState(null)
      if (window.location.pathname !== '/hub/login' && window.location.pathname !== '/scheduler/login') {
        window.location.href = '/hub/login'
      }
    }
    window.addEventListener('session-expired', handleSessionExpired)

    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current)
      }
      window.removeEventListener('session-expired', handleSessionExpired)
    }
  }, [user])

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = getUser()
    if (savedUser) {
      setUserState(savedUser)
      // Verify token is still valid
      authAPI.me()
        .then(data => {
          if (data.success && data.user) {
            setUserState(data.user)
            setUser(data.user)
          } else {
            clearAuth()
            setUserState(null)
          }
        })
        .catch(() => {
          clearAuth()
          setUserState(null)
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email, password, turnstileToken) => {
    try {
      const data = await authAPI.login(email, password, turnstileToken)
      if (data.success && data.user) {
        setUserState(data.user)
        setUser(data.user)
        // Refresh user data from /me endpoint to ensure we have complete user object
        // This ensures billing_enabled and other fields are available immediately
        // Add a small delay for Safari to ensure cookie is available
        try {
          // Wait a bit for Safari to process the cookie (especially important for SameSite cookies)
          await new Promise(resolve => setTimeout(resolve, 100))
          const meData = await authAPI.me()
          if (meData.success && meData.user) {
            setUserState(meData.user)
            setUser(meData.user)
            return { success: true, user: meData.user }
          }
        } catch (refreshError) {
          console.error('Error refreshing user data after login:', refreshError)
          // Don't fail login if /me fails - Safari might need more time for cookie
          // The login response already has user data, so we can continue with that
          // If it's a session expiration error, we'll handle it on the next request
          if (refreshError.sessionExpired) {
            // If session expired immediately, something is wrong - but don't clear auth yet
            // Let the user try to navigate, and if it fails then we'll handle it
            console.warn('Session expired immediately after login - may be Safari cookie issue')
          }
        }
        return { success: true, user: data.user }
      }
      return { success: false, error: 'Login failed' }
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' }
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUserState(null)
      clearAuth()
    }
  }

  const refreshUser = async () => {
    try {
      const data = await authAPI.me()
      if (data.success && data.user) {
        setUserState(data.user)
        setUser(data.user)
        return data.user
      }
      return null
    } catch (error) {
      console.error('Refresh user error:', error)
      // Only drop the session on auth failures — a transient /me error must not
      // log the user out right after a successful Profile password save.
      if (error?.status === 401 || error?.sessionExpired) {
        setUserState(null)
        clearAuth()
      }
      return null
    }
  }

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: isSchedulerAdmin(user),
    hasPermission: (permission) => hasPermission(user, permission),
    accessibleApps: getAccessibleApps(user),
    canAccessScheduler: hasPermission(user, HUB_APP_PERMISSIONS.room_scheduler),
  }

  return (
    <SchedulerAuthContext.Provider value={value}>
      {children}
    </SchedulerAuthContext.Provider>
  )
}

export function useSchedulerAuth() {
  const context = useContext(SchedulerAuthContext)
  if (!context) {
    throw new Error('useSchedulerAuth must be used within SchedulerAuthProvider')
  }
  return context
}
